import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useAddSupportMessage,
  useConvertSupportTicket,
  useCreateSupportTicket,
  useCustomers,
  usePriorities,
  useSupportStates,
  useSupportSummary,
  useSupportTicket,
  useSupportTickets,
  useUpdateSupportTicket,
  useUsers
} from '../api/queries'
import type {
  SupportMessage,
  SupportState,
  SupportTicket,
  SupportTicketInput,
  SupportType
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDateTime } from '../lib/format'

/**
 * Indbakken for det, der er meldt ind og endnu ikke besluttet
 * (docs/DECISIONS.md beslutning 073).
 *
 * <b>"Ny" står øverst og er rød.</b> En henvendelse, ingen har set på, er den
 * farlige - det er præcis dét, indbakken findes for at fange. En, der er i gang,
 * har nogen fat i.
 *
 * <b>Rækkerne er sorteret efter, hvor længe der er gået</b>, ikke efter hvornår
 * de kom ind. Den, der har ligget længst uden at der skete noget, er den, der er
 * ved at blive glemt.
 */
export function SupportPage() {
  const { isProjectManager } = useAuth()

  const [type, setType] = useState<SupportType | ''>('')
  const [state, setState] = useState<SupportState | ''>('')
  const [openOnly, setOpenOnly] = useState(true)
  const [creating, setCreating] = useState(false)

  const { data: summary } = useSupportSummary()
  const { data: states } = useSupportStates()
  const { data, isLoading, error } = useSupportTickets({
    type: type || undefined,
    state: state || undefined,
    openOnly
  })

  if (creating) {
    return <TicketForm onClose={() => setCreating(false)} />
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const tickets = data ?? []

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Henvendelser
        </h1>
        <button className="btn btn--primary" onClick={() => setCreating(true)}>
          Ny henvendelse
        </button>
      </div>

      {summary && (
        <Card>
          <div className="row" style={{ gap: 20, flexWrap: 'wrap' }}>
            <Stat label="Nye" value={summary.new} warn={summary.new > 0} />
            <Stat label="I gang" value={summary.open} />
            <Stat label="Afventer" value={summary.waiting} />
            <Stat label="Mine" value={summary.mineOpen} />
          </div>
          {summary.new > 0 && (
            <div className="tiny muted" style={{ marginTop: 8 }}>
              {summary.new === 1
                ? 'Én henvendelse har ingen set på endnu.'
                : `${summary.new} henvendelser har ingen set på endnu.`}
            </div>
          )}
        </Card>
      )}

      <Card>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={type} onChange={(event) => setType(event.target.value as SupportType | '')}>
            <option value="">Begge slags</option>
            <option value="customer">Kundehenvendelser</option>
            <option value="system">Problemer med systemet</option>
          </select>

          <select
            value={state}
            onChange={(event) => setState(event.target.value as SupportState | '')}
          >
            <option value="">Alle tilstande</option>
            {(states ?? []).map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>

          <label className="row" style={{ gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={openOnly}
              onChange={(event) => setOpenOnly(event.target.checked)}
              disabled={state !== ''}
            />
            <span className="small">Kun åbne</span>
          </label>
        </div>
      </Card>

      <Card title={`${tickets.length} henvendelser`}>
        {tickets.length === 0 ? (
          <EmptyState>
            Ingen henvendelser her. Det er stedet, hvor noget lander, før nogen har taget stilling
            til, om det bliver en sag, et servicebesøg eller en reklamation.
          </EmptyState>
        ) : (
          <div className="stack" style={{ gap: 2 }}>
            {tickets.map((ticket) => (
              <TicketRow key={ticket.id} ticket={ticket} />
            ))}
          </div>
        )}
      </Card>

      {!isProjectManager && (
        <div className="tiny muted">
          Du ser de henvendelser, du selv har meldt, og dem du er sat på.
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div>
      <div className="tiny muted">{label}</div>
      <div
        style={{
          fontWeight: 700,
          fontSize: 22,
          color: warn ? 'var(--danger, #b91c1c)' : undefined
        }}
      >
        {value}
      </div>
    </div>
  )
}

function TicketRow({ ticket }: { ticket: SupportTicket }) {
  return (
    <Link className="row row--between device-row" to={`/support/${ticket.id}`} style={{ gap: 10 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            className="tiny"
            style={{
              background: ticket.stateColor,
              color: '#fff',
              borderRadius: 4,
              padding: '1px 6px',
              whiteSpace: 'nowrap'
            }}
          >
            {ticket.stateName}
          </span>
          <span className="small" style={{ fontWeight: 600 }}>
            {ticket.subject}
          </span>
        </div>
        <div className="tiny muted">
          TC-{ticket.number} · {ticket.typeName}
          {ticket.customerName && ` · ${ticket.customerName}`}
          {ticket.assignee && ` · ${ticket.assignee.displayName}`}
          {ticket.source === 'mail' && ' · fra mail'}
          {ticket.messageCount > 1 && ` · ${ticket.messageCount} beskeder`}
        </div>
      </div>
      <div className="tiny muted" style={{ whiteSpace: 'nowrap' }}>
        {waited(ticket.waitingSinceHours)}
      </div>
    </Link>
  )
}

/**
 * Hvor længe der er gået, i noget man kan læse. Timer op til et døgn, så dage -
 * "37 timer" siger mindre end "2 dage".
 */
function waited(hours: number) {
  if (hours < 1) return 'lige nu'
  if (hours < 24) return `${Math.floor(hours)} t.`

  const days = Math.floor(hours / 24)
  return days === 1 ? '1 dag' : `${days} dage`
}

/** Én henvendelse med hele tråden. */
export function SupportTicketPage() {
  const { id } = useParams()
  const ticketId = Number(id)

  const { isProjectManager } = useAuth()

  const { data, isLoading, error } = useSupportTicket(ticketId)
  const { data: states } = useSupportStates()
  const { data: users } = useUsers()

  const update = useUpdateSupportTicket()
  const convert = useConvertSupportTicket()

  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Ordrenummeret til sagen - påkrævet som på alle nye sager (beslutning 077).
  const [orderNumber, setOrderNumber] = useState('')

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return null

  const { ticket, messages, canHandle } = data

  async function change(input: Parameters<typeof update.mutateAsync>[0]['input']) {
    setProblem(null)

    try {
      await update.mutateAsync({ id: ticketId, input })
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Ændringen kunne ikke gemmes.')
    }
  }

  async function toProject() {
    setProblem(null)
    setNotice(null)

    try {
      const result = await convert.mutateAsync({ id: ticketId, orderNumber })
      setNotice(`Sagen "${result.projectName}" er oprettet. Henvendelsen bliver stående og peger på den.`)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Sagen kunne ikke oprettes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="page-title" style={{ margin: 0 }}>
            {ticket.subject}
          </h1>
          <div className="muted small">
            TC-{ticket.number} · {ticket.typeName} · {ticket.sourceName} ·{' '}
            {formatDateTime(ticket.createdAt)}
          </div>
        </div>
        <Link className="btn btn--sm" to="/support">
          Til indbakken
        </Link>
      </div>

      {problem && <div className="error-box">{problem}</div>}
      {notice && <div className="map-notice">{notice}</div>}

      <Card>
        <div className="form-grid">
          <Field label="Tilstand">
            {canHandle ? (
              <select
                value={ticket.state}
                onChange={(event) => void change({ state: event.target.value as SupportState })}
              >
                {(states ?? []).map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="small">{ticket.stateName}</span>
            )}
          </Field>

          <Field label="Ansvarlig">
            {canHandle ? (
              <select
                value={ticket.assignee?.id ?? 0}
                onChange={(event) => void change({ assigneeId: Number(event.target.value) })}
              >
                <option value={0}>Ingen</option>
                {(users ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            ) : (
              <span className="small">{ticket.assignee?.displayName ?? 'Ingen'}</span>
            )}
          </Field>

          <Field label="Meldt af">
            {/* Den, der RINGEDE, staar foerst - ikke den, der tastede det ind.
                Ellers ser en henvendelse fra en kunde ud, som om en kollega
                havde meldt den. */}
            <span className="small">
              {ticket.reporterName ?? ticket.reportedBy?.displayName ?? 'Ukendt'}
            </span>
            {ticket.reporterName && ticket.reportedBy && (
              <div className="tiny muted">
                skrevet ind af {ticket.reportedBy.displayName}
              </div>
            )}
            {ticket.reporterEmail && (
              <div className="tiny muted">
                <a href={`mailto:${ticket.reporterEmail}`}>{ticket.reporterEmail}</a>
              </div>
            )}
            {ticket.reporterPhone && (
              <div className="tiny muted">
                <a href={`tel:${ticket.reporterPhone.replace(/\s/g, '')}`}>{ticket.reporterPhone}</a>
              </div>
            )}
          </Field>

          <Field label="Kunde">
            <span className="small">{ticket.customerName ?? '-'}</span>
          </Field>
        </div>

        {ticket.projectId ? (
          <div className="map-notice" style={{ marginTop: 12 }}>
            {ticket.convertedTo ?? 'Hører til en sag'} -{' '}
            <Link to={`/projects/${ticket.projectId}`}>åbn sagen</Link>
          </div>
        ) : (
          isProjectManager && (
            <form
              style={{ marginTop: 14 }}
              onSubmit={(event) => {
                event.preventDefault()
                void toProject()
              }}
            >
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input
                  aria-label="Ordrenummer"
                  placeholder="Ordrenummer *"
                  required
                  pattern="[0-9]+(-[0-9]+)*"
                  title="Cifre og bindestreg, som nummeret står i Ordrestyring - f.eks. 4850-1"
                  style={{ width: 170 }}
                  value={orderNumber}
                  onChange={(event) => setOrderNumber(event.target.value)}
                />
                <button className="btn" type="submit" disabled={convert.isPending}>
                  {convert.isPending ? 'Opretter ...' : 'Gør til en sag'}
                </button>
              </div>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Henvendelsen bliver stående og peger på sagen - skriver kunden igen, lander svaret
                stadig her. Kræver, at der er valgt en kunde og skrevet sagens ordrenummer fra
                Ordrestyring.
              </div>
            </form>
          )
        )}
      </Card>

      <Card title={`Tråden (${messages.length})`}>
        <div className="stack" style={{ gap: 12 }}>
          {messages.map((message) => (
            <MessageRow key={message.id} message={message} />
          ))}
        </div>
      </Card>

      <ReplyCard ticketId={ticketId} canHandle={canHandle} hasAddress={Boolean(ticket.reporterEmail)} />
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="tiny muted">{label}</div>
      {children}
    </div>
  )
}

function MessageRow({ message }: { message: SupportMessage }) {
  const who = message.authorUser?.displayName ?? message.authorName ?? message.authorEmail ?? 'Ukendt'

  return (
    <div
      style={{
        borderLeft: `3px solid ${message.isInternal ? '#D97706' : '#CBD5E1'}`,
        paddingLeft: 10
      }}
    >
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {message.authorUser && <Avatar user={message.authorUser} size={22} />}
        <span className="small" style={{ fontWeight: 600 }}>
          {who}
        </span>
        <span className="tiny muted">{formatDateTime(message.createdAt)}</span>
        {message.isInternal && (
          <span className="tiny" style={{ color: '#D97706', fontWeight: 600 }}>
            intern note
          </span>
        )}
        {message.source === 'mail' && <span className="tiny muted">fra mail</span>}
      </div>

      {/* Teksten er ren tekst og vises som ren tekst. En mail udefra er skrevet
          af en fremmed - blev den til HTML, kunne den koere noget i browseren
          (beslutning 073). */}
      <div className="small" style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
        {message.body}
      </div>

      {message.attachmentCount > 0 && (
        <div className="tiny muted" style={{ marginTop: 4 }}>
          📎 Mailen havde vedhæftede filer. De hentes ikke ind i systemet - de ligger i postkassen.
        </div>
      )}
    </div>
  )
}

function ReplyCard({
  ticketId,
  canHandle,
  hasAddress
}: {
  ticketId: number
  canHandle: boolean
  hasAddress: boolean
}) {
  const add = useAddSupportMessage()

  const [body, setBody] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function send() {
    setProblem(null)
    setNotice(null)

    try {
      const result = await add.mutateAsync({ id: ticketId, body, isInternal })
      setBody('')

      if (isInternal) {
        setNotice('Noten er skrevet. Den sendes ikke ud af huset.')
      } else if (result.mailSent) {
        setNotice('Svaret er skrevet og sendt som mail.')
      } else {
        // Beskeden ER gemt - en fejlet mail maa ikke koste det, nogen skrev.
        setNotice(result.mailError ?? 'Svaret er gemt, men blev ikke sendt som mail.')
      }
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Beskeden kunne ikke gemmes.')
    }
  }

  return (
    <Card title="Skriv">
      <textarea
        rows={5}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder={isInternal ? 'En note kun vi ser ...' : 'Svar til den, der meldte det ...'}
        style={{ width: '100%' }}
      />

      {canHandle && (
        <label className="row" style={{ gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input
            type="checkbox"
            checked={isInternal}
            onChange={(event) => setIsInternal(event.target.checked)}
          />
          <span className="small">Intern note - sendes ikke ud af huset</span>
        </label>
      )}

      {!isInternal && !hasAddress && (
        <div className="tiny muted" style={{ marginTop: 8 }}>
          Der er ingen mailadresse på henvendelsen, så svaret bliver kun gemt her.
        </div>
      )}

      <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          className="btn btn--primary"
          disabled={add.isPending || body.trim().length === 0}
          onClick={() => void send()}
        >
          {add.isPending ? 'Sender ...' : isInternal ? 'Gem noten' : 'Send svar'}
        </button>
      </div>

      {problem && <div className="error-box" style={{ marginTop: 12 }}>{problem}</div>}
      {notice && <div className="map-notice" style={{ marginTop: 12 }}>{notice}</div>}
    </Card>
  )
}

/** Ny henvendelse - typisk den, der tog telefonen. */
function TicketForm({ onClose }: { onClose: () => void }) {
  const { isProjectManager } = useAuth()
  const navigate = useNavigate()

  const create = useCreateSupportTicket()
  const { data: customers } = useCustomers()
  const { data: priorities } = usePriorities()

  const [form, setForm] = useState<SupportTicketInput>({
    type: 'customer',
    subject: '',
    body: '',
    priorityId: null,
    customerId: null,
    reporterName: '',
    reporterEmail: '',
    reporterPhone: ''
  })

  const [problem, setProblem] = useState<string | null>(null)

  function set<K extends keyof SupportTicketInput>(key: K, value: SupportTicketInput[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function submit() {
    setProblem(null)

    try {
      const ticket = await create.mutateAsync(form)
      navigate(`/support/${ticket.id}`)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Henvendelsen kunne ikke oprettes.')
    }
  }

  const isCustomer = form.type === 'customer'

  return (
    <div className="stack" style={{ gap: 16 }}>
      <h1 className="page-title" style={{ margin: 0 }}>
        Ny henvendelse
      </h1>

      <Card title="Hvad er det?">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button
            className={`btn ${isCustomer ? 'btn--primary' : ''}`}
            onClick={() => set('type', 'customer')}
            style={{ flex: '1 1 180px', minHeight: 48 }}
          >
            📞 Kundehenvendelse
          </button>
          <button
            className={`btn ${!isCustomer ? 'btn--primary' : ''}`}
            onClick={() => set('type', 'system')}
            style={{ flex: '1 1 180px', minHeight: 48 }}
          >
            🧰 Problem med systemet
          </button>
        </div>

        <div className="small muted" style={{ marginTop: 8 }}>
          {isCustomer
            ? 'Noget kunden har meldt. Det kan senere gøres til en sag.'
            : 'Noget du ikke kan få systemet til at gøre. Går til administratorerne.'}
        </div>
      </Card>

      <Card title="Hvad drejer det sig om?">
        <div>
          <div className="tiny muted">Emne</div>
          <input
            value={form.subject}
            onChange={(event) => set('subject', event.target.value)}
            placeholder={isCustomer ? 'Lyset virker ikke i opgang B' : 'Scanneren åbner ikke kameraet'}
            maxLength={200}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <div className="tiny muted">Beskrivelse</div>
          <textarea
            rows={5}
            value={form.body}
            onChange={(event) => set('body', event.target.value)}
            placeholder="Skriv hvad der blev sagt ..."
            style={{ width: '100%' }}
          />
        </div>

        {isProjectManager && (
          <div className="form-grid" style={{ marginTop: 12 }}>
            <div>
              <div className="tiny muted">Prioritet</div>
              <select
                value={form.priorityId ?? ''}
                onChange={(event) =>
                  set('priorityId', event.target.value ? Number(event.target.value) : null)
                }
              >
                <option value="">Ikke sat</option>
                {(priorities ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </Card>

      {isCustomer && (
        <Card title="Hvem ringede?">
          <div className="form-grid">
            <div>
              <div className="tiny muted">Kunde</div>
              <select
                value={form.customerId ?? ''}
                onChange={(event) =>
                  set('customerId', event.target.value ? Number(event.target.value) : null)
                }
              >
                <option value="">Vælg kunde ...</option>
                {(customers ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <div className="tiny muted" style={{ marginTop: 4 }}>
                Skal være valgt, før henvendelsen kan gøres til en sag.
              </div>
            </div>

            <div>
              <div className="tiny muted">Navn</div>
              <input
                value={form.reporterName ?? ''}
                onChange={(event) => set('reporterName', event.target.value)}
                maxLength={200}
              />
            </div>

            <div>
              <div className="tiny muted">Mail</div>
              <input
                type="email"
                value={form.reporterEmail ?? ''}
                onChange={(event) => set('reporterEmail', event.target.value)}
                maxLength={200}
              />
              <div className="tiny muted" style={{ marginTop: 4 }}>
                Uden en adresse kan der ikke svares på mail herfra.
              </div>
            </div>

            <div>
              <div className="tiny muted">Telefon</div>
              <input
                type="tel"
                value={form.reporterPhone ?? ''}
                onChange={(event) => set('reporterPhone', event.target.value)}
                maxLength={40}
              />
            </div>
          </div>
        </Card>
      )}

      {problem && <div className="error-box">{problem}</div>}

      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn--primary" disabled={create.isPending} onClick={() => void submit()}>
          {create.isPending ? 'Opretter ...' : 'Opret'}
        </button>
        <button className="btn" onClick={onClose}>
          Fortryd
        </button>
      </div>
    </div>
  )
}
